import { z } from "zod";

/**
 * Единая квота расхода ИИ в условных кредитах (решение владельца 08.09.2026).
 *
 * До этого дня потолков было три и они стояли врозь: знаки озвучки
 * (`TTS_MONTHLY_CHARS`), нажатия сочинения (`COMPOSE_MONTHLY_MAX`) и листы
 * вопросов (`LISTENING_QUESTIONS_MONTHLY_MAX`), плюс общий потолок токенов
 * четвёртым. Кошелёк при этом был ОДИН: платит наш ключ у прокси AI-контура, и
 * три счётчика отвечали на вопрос «сколько я могу ещё» тремя разными числами,
 * ни одно из которых не было тем, что человека на самом деле остановит.
 *
 * ⚠️ **Полоска в боковой панели обязана показывать то, что останавливает.**
 * Отсюда одна квота и одна единица: кредит.
 *
 * **Три правила этого файла, каждое закрывает свою поломку.**
 *
 * 1. **Внутри всё считается в МИКРОДОЛЛАРАХ целым числом.** Кредит это только
 *    показ. Доллары числом с плавающей точкой копятся с ошибкой, а расход одного
 *    вызова это единицы микродолларов: складывать такое сотнями раз в месяц
 *    значит получить счётчик, который расходится с настоящим счётом тем сильнее,
 *    чем дольше живёт
 * 2. **Стоимость вызова модели берётся у ПРОКСИ**, а не оценивается нами
 *    (`x-litellm-response-cost`, разбор в `apps/api/src/ai/cost.ts`). Прайс в
 *    нашем коде означал бы, что цифра врёт молча при первой же смене модели в
 *    `litellm.yaml`, а по правилу 5.9 CLAUDE.md наш код знает только имя ГРУППЫ
 *    моделей
 * 3. **Озвучка считается по ЗНАКАМ** (`TTS_PRICE_USD_PER_1000_CHARS` в
 *    `./admin-tts.ts`), потому что у аудио-эндпоинта заголовка стоимости нет
 *    вовсе. Пересчёт знаков в микродоллары живёт на сервере
 *    (`apps/api/src/repo/ai-credits.ts`): браузеру он не нужен ни разу, а этот
 *    файл читает боковая панель, то есть СТАРТОВЫЙ чанк
 *
 * ⚠️ **Поэтому здесь нет ни одной тяжёлой зависимости и ни одного словаря.**
 * Два числа, четыре арифметические функции и одна схема ответа. Всё, что весит,
 * держится по ту сторону: цена озвучки в `./admin-tts.ts`, разрез панели в
 * `./admin-ai-credits.ts`, чтение таблиц на сервере.
 *
 * ⚠️ **Средней цены нажатия здесь больше нет, и убрана она нарочно.** До
 * появления полоски файл нёс две оценки (`COMPOSE_TYPICAL_CREDITS`,
 * `QUESTIONS_TYPICAL_CREDITS`) и функцию, переводившую остаток кошелька в число
 * нажатий: панель сочинения писала «осталось 198 сочинений», панель вычитки
 * «осталось 198 наборов вопросов». Числа эти были выдуманы средним по десятку
 * вызовов, а останавливает человека кошелёк, и остаток у него один на три
 * кнопки. Полоска в боковой панели показывает ровно то, что останавливает,
 * поэтому второго счётчика, считающего то же самое иначе, в продукте нет.
 */

/** Микродолларов в долларе. Целое, чтобы деньги нигде не были дробными. */
export const MICRO_USD_PER_USD = 1_000_000;

/**
 * Цена одного кредита: ЧЕТВЕРТЬ цента себестоимости, то есть тысяча кредитов
 * это $2,50.
 *
 * ⚠️ **Это ЕДИНСТВЕННОЕ место, где история и смысл пега записаны целиком.**
 * Остальные упоминания в коде, шаблонах и документах ссылаются сюда и повторяют
 * максимум одно число. Так сделано после ревью 10.10.2026: первая редакция
 * пересказывала эту историю тринадцатью копиями, и копии разъехались между собой
 * в тот же день, ещё до первого коммита.
 *
 * ⚠️ **Кредит это НЕ рубль и не то, что человек нам платит.** Это наша
 * себестоимость, огрублённая до величины, которую можно показать не пугая: счёт
 * в долларах читается как «0,0125», а в кредитах как «5». Продукт при этом
 * остаётся payment-agnostic (раздел 6 CLAUDE.md): денег он не трогает, он их
 * только считает.
 *
 * ⚠️ **Сменён 10.10.2026 решением владельца с цента на четверть цента, и
 * причина экономическая.** Квота 1000 кредитов при центовом пеге означала $10
 * себестоимости в месяц на преподавателя, то есть больше плоского рейта,
 * который владелец планирует: один человек, выбравший свою квоту до конца,
 * съедал БОЛЬШЕ всей выручки с себя. При четверти цента та же тысяча это $2,50.
 * ⚠️ **Рейта, ставок и счёта безубыточности здесь НЕТ намеренно**: цена живёт в
 * `docs/product-strategy.md`, раздел 6 (запись 10.10.2026), и само решение о
 * цене на тот день ещё не принято. Арифметика, вписанная сюда, была бы вторым
 * местом, где лежит цена продукта, и разошлась бы с первым.
 *
 * Квота числом не тронута (`AI_MONTHLY_CREDITS` = 1000) намеренно: менялась цена
 * единицы, а не её количество.
 *
 * ⚠️ **Но «человек ничего не заметил» это НЕПРАВДА, и говорить так нельзя.** Та
 * же тысяча осталась только в полоске кредитов (`SideCredits`): там и правда
 * стоит «занято N из 1000». А запас ОЗВУЧКИ человеку показывается в ЗНАКАХ и
 * минутах, и считается он из той же квоты (`monthlyChars` в ответе
 * `/v1/tts/voices`, `apps/api/src/routes/tts.ts`), поэтому ужался вчетверо
 * вместе с пегом: 25 000 знаков вместо 100 000, то есть около 16 полных треков
 * по 1500 знаков вместо 66. Это видно глазами на экране заказа озвучки, и
 * пересчёт квоты в знаки живёт в `ttsCharsOfMicroUsd`.
 *
 * ⚠️ **Записи в «Что нового» про это НЕТ, и это решение владельца 10.10.2026, а
 * не забывчивость.** Запись про уменьшение запаса осталась бы в списке
 * НАВСЕГДА, в том числе у тех, кто прежнего запаса не видел вовсе, то есть
 * сообщала бы потерю людям, которые ничего не теряли. Тем, кто видел, владелец
 * скажет сам и лично. Заводя такую запись «для порядка», сначала перечитайте
 * это.
 *
 * ⚠️ **Чего пег НЕ защищает, и это надо знать до того, как на него сошлются.**
 * Пег задаёт РАЗМЕР бюджета, а не стабильность обещания: он денежный, поэтому
 * квота держит постоянными НАШИ деньги, а не запас работы у преподавателя. С
 * 01.01.2027 промо-цена Gemini Flash кончается и ставки удваиваются, и дальше
 * всё зависит от того, УЗНАЕТ ли прокси новую цену. Ветки две, и они дают
 * противоположные поломки:
 *
 * - **карту цен прокси обновили:** заголовок `x-litellm-response-cost` называет
 *   настоящую цену, наши $2,50 на человека остаются, а нажатий в тысяче
 *   кредитов становится вдвое меньше. Молча и у всех сразу;
 * - **карту цен прокси не обновили:** заголовок называет промо-цену и занижает
 *   МОДЕЛЬНУЮ долю расхода вдвое, нажатий у преподавателя остаётся столько же, а
 *   наш счёт растёт. Квота перестаёт быть оградой ровно в той половине, ради
 *   которой она и стоит. ⚠️ **«Тысяча встанет нам в $5» это ВЕРХНЯЯ граница, а
 *   не исход:** через заголовок едет только то, что считает прокси, а озвучка
 *   считается нашей собственной `TTS_PRICE_USD_PER_1000_CHARS` и занизить не
 *   может вовсе. На смеси, где главный едок квоты это ElevenLabs
 *   (`docs/model-choice-research.md`, раздел 3: текст и звук в комплекте стоят
 *   почти поровну, а на дешёвой модели звук остаётся единственным едоком),
 *   перерасход заметно меньше двух раз.
 *
 * ⚠️ **Сегодня действует ВТОРАЯ ветка:** образ прокси `v1.101.0` знает
 * промо-цену (найдено расчётом 10.10.2026, строка во «Входящем»
 * `docs/WORK.md`; то же сказано заранее в `docs/model-choice-research.md`,
 * раздел 7: «свежая карта цен знает сегодняшнюю цену и с 01.01.2027 соврёт
 * ровно так же, как соврала бы старая»). То есть к январю надо не «посмотреть, как упадут нажатия», а
 * обновить карту цен образа, и уже после этого выбирать между нажатиями и
 * счётом. Хотим постоянства РАБОТЫ, а не постоянства денег, придётся считать
 * квоту в нажатиях, и это другое решение, а не настройка этого числа.
 *
 * **Порядок величин на 10.10.2026, и у каждого числа названо происхождение**,
 * потому что они разной крепости и путать их дорого:
 *
 * - **сочинение диалога около 5 кредитов** — ОЦЕНКА по прайсу Gemini Flash
 *   (`docs/model-choice-research.md`, раздел 3), грубо подтверждённая живым
 *   замером 15.09.2026: два заказа сдвинули счётчик на один СТАРЫЙ кредит
 *   каждый, а старый кредит это четыре новых;
 * - **лист вопросов к аудированию около 8 кредитов** — тоже ОЦЕНКА по тому же
 *   прайсу, и на Gemini он **не мерен ни разу** (сказано там же). Единственный
 *   его замер это 08.09.2026 эпохи Sonnet, и он давал около 40 новых кредитов,
 *   впятеро больше. Порог `AI_MONTHLY_CREDITS_MIN` стоит по ЗАМЕРУ, а не по
 *   оценке, и расхождение это не ошибка, а запас;
 * - **тысяча знаков озвучки ровно 40 кредитов** — не оценка вовсе, считается из
 *   `TTS_PRICE_USD_PER_1000_CHARS` (`./admin-tts.ts`), сверенной по счёту
 *   провайдера 06.09.2026;
 * - **страница распознавания около 1 кредита** — ЗАМЕР 07.10.2026, два кадра с
 *   текстом из трёх: 2275 и 3300 микродолларов, то есть 0,9 и 1,3 кредита.
 *   ⚠️ Синтетический кадр того же замера (`at-ceiling.png`, 960 микродолларов)
 *   в этот счёт не входит, и исключён он не ради красоты диапазона: текста на
 *   нём нет вовсе, исход `empty`, то есть цена страницы по нему не мерится.
 *   Оценка того же проекта (решение 17, 2000 токенов выхода) давала 3,6
 *   кредита, то есть была пессимистична примерно вчетверо ПО ЦЕНЕ (3,6 против
 *   замеренных 0,9-1,3, то есть в 2,7-4 раза), и причина в длине выхода: 378 и
 *   666 токенов против оценочных 2000.
 */
export const AI_CREDIT_MICRO_USD = 2_500;

/**
 * Доллары в микродоллары.
 *
 * ⚠️ **Округление обязательно, и вниз округлять нельзя.** На вход приходит
 * `1.3e-05` строкой из заголовка прокси, то есть 13 микродолларов; усечение
 * дало бы 12 или 13 в зависимости от того, как легло двоичное представление, и
 * счётчик расходился бы со счётом провайдера тем сильнее, чем больше вызовов.
 *
 * Отрицательного не бывает: заголовок с минусом это поломка на той стороне, а
 * не возврат денег, и уменьшать им расход значило бы отдать бесплатные вызовы
 * тому, кто научится его подделывать.
 */
export function microUsdOfUsd(usd: number): number {
  if (!Number.isFinite(usd) || usd <= 0) return 0;
  return Math.round(usd * MICRO_USD_PER_USD);
}

/** Кредиты в микродоллары. Кредит целый, поэтому пересчёт точный. */
export function microUsdOfCredits(credits: number): number {
  return Math.max(Math.round(credits * AI_CREDIT_MICRO_USD), 0);
}

/**
 * Микродоллары в кредиты для ПОКАЗА, с точностью до сотой.
 *
 * Дробь нужна только разрезу панели: там строка одного человека бывает в
 * полкредита, и округление до целого превратило бы её в ноль рядом с ненулевым
 * итогом. Преподавателю дробей не показывается ни одной, ему считает
 * `creditsSpentOf`.
 */
export function creditsOfMicroUsd(microUsd: number): number {
  return Math.round((Math.max(microUsd, 0) / AI_CREDIT_MICRO_USD) * 100) / 100;
}

/**
 * Потраченное в кредитах, целым числом и ВВЕРХ.
 *
 * ⚠️ Вверх, а не к ближайшему: остаток, посчитанный из округлённого вниз
 * расхода, обещал бы кредит, которого уже нет, и последнее нажатие месяца
 * упиралось бы в отказ при полоске, показывающей «ещё есть».
 */
export function creditsSpentOf(microUsd: number): number {
  return Math.ceil(Math.max(microUsd, 0) / AI_CREDIT_MICRO_USD);
}

// ------------------------------------------------------------------ ручка

/**
 * Остаток кредитов для боковой панели: `GET /v1/ai/credits`.
 *
 * ⚠️ **Отдаётся преподавателю про СЕБЯ и только ему.** Ученику эта ручка не
 * должна быть даже видна, поэтому отказ там 404, а не 403 (раздел 9 CLAUDE.md).
 *
 * ⚠️ **Расход тут показывается, и правилу «в адрес преподавателя не считается
 * ничего» (5.10 CLAUDE.md) это не противоречит.** Правило про его РАБОТУ:
 * покрытие программы, пройденные темы, число проверенных домашек. Здесь
 * считается израсходованный РЕСУРС, который мы ему выдали, и знаменатель у него
 * наш, а не его. Тот же довод уже записан у `monthlyChars` в `./tts.ts`.
 */
export const AiCreditsResponse = z.object({
  /** Размер месячной квоты. Без знаменателя остаток не с чем сравнить. */
  quota: z.number().int().positive(),
  /**
   * Занято в этом месяце.
   *
   * ⚠️ **Идущая озвучка входит сюда наравне с оплаченной**, и это не ошибка
   * счёта. Задача, которая прямо сейчас говорит с провайдером, за свои знаки уже
   * отвечает, и показать их свободными значило бы дать заказать вторую озвучку
   * под тот же остаток. Отменённая на полпути задача возвращает незатраченное, и
   * число уменьшается обратно.
   *
   * ⚠️ Может оказаться БОЛЬШЕ квоты: квоту можно уменьшить настройкой, а уже
   * потраченное от этого не исчезнет. Остаток при этом ноль, а не минус.
   */
  spent: z.number().int().nonnegative(),
  /** Сколько осталось. Никогда не отрицательно, см. `spent`. */
  left: z.number().int().nonnegative(),
  /** Месяц учёта, `ГГГГ-ММ`. Календарный и по UTC, см. `ttsUsageMonthOf`. */
  month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),
});
export type AiCreditsResponse = z.infer<typeof AiCreditsResponse>;

/**
 * Что человек читает, когда кредиты кончились.
 *
 * ⚠️ **Строка ОДНА на три кнопки, и это главное следствие единой квоты.** До
 * 08.09.2026 их было три («Диалоги на этот месяц закончились», «Вопросы на этот
 * месяц закончились», «На этот месяц озвучка закончилась»), и каждая была
 * правдой при своём потолке. С одним кошельком любая из них соврала бы дважды:
 * человек пошёл бы искать, где у него кончились именно диалоги, и не нашёл бы,
 * а озвучку, вставшую от вычерпанных диалогов, объяснить было бы нечем.
 *
 * Коды отказа при этом остались прежними и разными (`compose_quota_exceeded`,
 * `questions_quota_exceeded`, `tts_quota_exceeded`): по ним видно в логе, на
 * какой кнопке человек остановился, а это разные разговоры про то, куда ушли
 * деньги. Один текст на три кода это не склейка кодов, а совпадение совета.
 */
export const AI_CREDITS_EXCEEDED_TEXT =
  "Кредиты на этот месяц закончились, продолжим с первого числа";
